import { startOfMonth } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAbilityContext } from '~/components/Can';
// FloatingActionButton removed from nav; handled at view level
import classNames from 'classnames';
import { MonthDropdown } from '~/components/layout/CalendarLayout';
import { Navbar } from '~/components/nav/Navbar';
import {
  NavSettingsMenuItem,
  NavSettingsPanel,
} from '~/components/ui/NavSettingsPanel';
import { MenuItem, TopBar } from '~/components/ui/TopBar';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { NavLogo } from './NavLogo';

/** Convert translation key to route path */
const toRoutePath = (key: string) => {
  switch (key) {
    case 'nav:jobs.job-templates':
      return '/job-templates';
    case 'nav:jobs.automations':
      return '/automations';
    case 'nav:jobs.planner':
      return '/jobs/planner';
    case 'nav:jobs.calendar':
      // Calendar tab should go to the monthly calendar root
      return '/jobs/calendar';
    default:
      return `/jobs/${key.substring('nav:jobs.'.length)}`;
  }
};

const getNavItems = () => {
  const items = [
    'nav:jobs.planner',
    'nav:jobs.calendar',
    'nav:jobs.scheduled',
    'nav:jobs.unscheduled',
    'nav:jobs.completed',
  ];
  /* if (hasScheduledAccess) {
    items.unshift('nav:jobs.calendar');
  } */
  return items;
};

/** Route helpers */
const isOnSettingsPage = (pathname: string) =>
  Boolean(
    matchPath({ path: '/job-templates/*', end: false }, pathname) ||
      matchPath({ path: '/automations/*', end: false }, pathname)
  );

const getLastJobsPathOrDefault = (
  getValue: (key: string) => string | null | undefined
) => {
  const lastJobsPath = getValue('nav.jobs');
  return lastJobsPath && lastJobsPath.startsWith('/jobs/')
    ? lastJobsPath
    : '/jobs/scheduled';
};

// type FabAction = { to: string; label: string };

// FAB logic moved to view-level AddButton

// FAB logic moved to view-level AddButton

type MonthlyControls = {
  currentDate: Date;
  onMonthSelect: (date: Date) => void;
};

type SelectModeNavProps = {
  selectMode?: boolean;
  onSelectJobs?: () => void;
};

/** Shared navigation logic hook */
const useJobsNavigation = () => {
  const { t } = useTranslation(['translation', 'nav']);
  const ability = useAbilityContext();
  const localStorage = useLocalStorage();
  const location = useLocation();
  const navigate = useNavigate();

  // Authorization is intentionally disabled for this view while pricing is being implemented.

  /*   const hasScheduledAccess = ability.can('read', 'feat.schedule_view'); */
  /*   const navItems = getNavItems(hasScheduledAccess); */
  const navItems = useMemo(() => getNavItems(), []);
  const isOwner = ability.can('manage', 'Account');
  const hasTemplates = isOwner;
  const hasAutomations = isOwner;

  const remember = (pathname: string) => {
    if (
      pathname &&
      (matchPath({ path: '/jobs', end: false }, pathname) ||
        matchPath({ path: '/job-templates', end: false }, pathname) ||
        matchPath({ path: '/automations', end: false }, pathname))
    ) {
      localStorage.set('nav.jobs', pathname);
    }
  };

  return {
    t,
    ability,
    localStorage,
    location,
    navigate,
    navItems,
    isOwner,
    hasTemplates,
    hasAutomations,
    remember,
  };
};

/** Create templates menu item */
const createTemplatesMenuItem = (
  t: (key: string) => string,
  hasTemplates: boolean,
  remember: (pathname: string) => void,
  isSettingsItem = false
) => {
  if (!hasTemplates) return [];

  const baseItem = {
    key: 'job-templates',
    label: t('nav:jobs.job-templates'),
    to: '/job-templates',
    'data-usetiful': 'jobs-nav-job-templates',
  };

  if (isSettingsItem) {
    return [
      {
        ...baseItem,
        activeKey: 'nav:jobs.job-templates',
        onClick: () => remember('/job-templates'),
      },
    ];
  }

  return [
    {
      ...baseItem,
      isConfigItem: true,
    },
  ];
};

/** Create automations menu item */
const createAutomationsMenuItem = (
  t: (key: string) => string,
  hasAutomations: boolean,
  remember: (pathname: string) => void,
  isSettingsItem = false
) => {
  if (!hasAutomations) return [];

  const baseItem = {
    key: 'automations',
    label: t('nav:jobs.automations'),
    to: '/automations',
    'data-usetiful': 'jobs-nav-automations',
  };

  if (isSettingsItem) {
    return [
      {
        ...baseItem,
        activeKey: 'nav:jobs.automations',
        onClick: () => remember('/automations'),
      },
    ];
  }

  return [
    {
      ...baseItem,
      isConfigItem: true,
    },
  ];
};

// renderAddButton moved to view level

const isJobListPath = (pathname: string) =>
  Boolean(
    matchPath({ path: '/jobs/scheduled', end: false }, pathname) ||
      matchPath({ path: '/jobs/unscheduled', end: false }, pathname) ||
      matchPath({ path: '/jobs/completed', end: false }, pathname)
  );

const Mobile = ({
  monthlyControls,
  selectMode,
  onSelectJobs,
}: {
  monthlyControls?: MonthlyControls;
} & SelectModeNavProps) => {
  const { t, location, navItems, hasTemplates, hasAutomations, remember } =
    useJobsNavigation();

  const getCurrentPageName = () => {
    const p = location.pathname;
    if (matchPath({ path: '/jobs/calendar', end: false }, p))
      return t('nav:jobs.calendar');
    if (matchPath({ path: '/jobs/planner', end: false }, p))
      return t('translation:planner');
    if (matchPath({ path: '/jobs/scheduled', end: false }, p))
      return t('nav:jobs.scheduled');
    if (matchPath({ path: '/jobs/unscheduled', end: false }, p))
      return t('nav:jobs.unscheduled');
    if (matchPath({ path: '/jobs/completed', end: false }, p))
      return t('nav:jobs.completed');
    if (matchPath({ path: '/automations', end: false }, p))
      return t('nav:jobs.automations');
    if (matchPath({ path: '/job-templates', end: false }, p))
      return t('nav:jobs.job-templates');
    return t('nav:jobs.all');
  };

  const showSelectJobs =
    Boolean(onSelectJobs) && isJobListPath(location.pathname);

  // Create menu items for the TopBar
  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [
      ...navItems.map((key: string) => ({
        key,
        label: key === 'nav:jobs.planner' ? t('translation:planner') : t(key),
        to: toRoutePath(key),
      })),
      ...createAutomationsMenuItem(t, hasAutomations, remember, false),
      ...createTemplatesMenuItem(t, hasTemplates, remember, false),
    ];
    if (showSelectJobs && !selectMode) {
      items.push({
        key: 'jobs.select',
        label: t('translation:selectJobs', { defaultValue: 'Select jobs' }),
        onClick: onSelectJobs,
      });
    }
    return items;
  }, [
    navItems,
    t,
    hasTemplates,
    hasAutomations,
    remember,
    showSelectJobs,
    selectMode,
    onSelectJobs,
  ]);

  const isCalendar = location.pathname.startsWith('/jobs/calendar');

  const titleNode =
    isCalendar && monthlyControls ? (
      <div className='flex items-center justify-center'>
        <MonthDropdown
          currentDate={monthlyControls.currentDate}
          onMonthSelect={(d) => monthlyControls.onMonthSelect(startOfMonth(d))}
          variant='topbar'
        />
      </div>
    ) : (
      getCurrentPageName()
    );

  return (
    <nav className='lg:hidden'>
      <TopBar
        title={titleNode}
        menuItems={selectMode ? undefined : menuItems}
        onMenuItemClick={(item) => {
          if (item.to) {
            remember(item.to);
          }
        }}
      />
    </nav>
  );
};

const Desktop = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unreadActivity,
}: {
  unreadActivity?: number;
}) => {
  const {
    t,
    localStorage,
    location,
    navigate,
    navItems,
    isOwner,
    hasTemplates,
    hasAutomations,
    remember,
  } = useJobsNavigation();

  // Initialize settings state - only read from localStorage
  const [showSettings, setShowSettings] = useState(() => {
    try {
      const saved = localStorage.get('nav.jobs.settings');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Custom setter that saves to localStorage immediately
  const setShowSettingsAndSave = useCallback(
    (newValue: boolean) => {
      setShowSettings(newValue);
      try {
        localStorage.set('nav.jobs.settings', newValue.toString());
      } catch {
        // Ignore localStorage errors
      }

      // If closing settings and currently on a settings page, redirect to default
      if (!newValue) {
        if (isOnSettingsPage(location.pathname)) {
          const defaultPath = getLastJobsPathOrDefault(localStorage.get);
          navigate(defaultPath);
        }
      }
    },
    [localStorage, location.pathname, navigate]
  );

  const shouldShowSettings = showSettings;
  const hasSettingsItems = hasTemplates || hasAutomations;

  // Check if settings are closed but we're on a settings page (e.g., on page load)
  // Only run once on mount to avoid duplicate navigation
  const hasRunRedirectRef = useRef(false);
  useEffect(() => {
    if (hasRunRedirectRef.current) return;
    if (!showSettings && isOnSettingsPage(location.pathname)) {
      hasRunRedirectRef.current = true;
      const defaultPath = getLastJobsPathOrDefault(localStorage.get);
      navigate(defaultPath);
    }
  }, [showSettings, location.pathname, localStorage, navigate]);

  // Create settings menu items
  const settingsMenuItems: NavSettingsMenuItem[] = useMemo(
    () => [
      ...createTemplatesMenuItem(t, hasTemplates, remember, true),
      ...createAutomationsMenuItem(t, hasAutomations, remember, true),
    ],
    [t, hasTemplates, hasAutomations, remember]
  );
  return (
    <div
      data-usetiful='jobs-nav'
      className='z-50 hidden items-center bg-white lg:flex lg:border-b lg:border-outline-bright'
    >
      <NavLogo />
      <Navbar
        hasSettingsPanel={isOwner && hasSettingsItems}
        className='overflow-x-hidden border-b-0 lg:pl-12 xl:pl-20 '
      >
        <div
          className={classNames(
            'flex min-w-0 items-stretch',
            shouldShowSettings && isOwner && hasSettingsItems
              ? 'relative z-10 rounded-r-full bg-white pr-6 after:pointer-events-none after:absolute after:-inset-0.5 after:rounded-r-full after:border after:border-l-0 after:border-outline-bright'
              : 'pr-2'
          )}
        >
          {navItems.map((key: string) => {
            // Don't show nav items as active when we're on settings pages
            const to = toRoutePath(key);
            const isActive =
              !isOnSettingsPage(location.pathname) &&
              Boolean(matchPath({ path: to, end: false }, location.pathname));

            return (
              <Navbar.Item
                key={key}
                data-usetiful={`${key}`}
                as='button'
                className={classNames(
                  'border-b-4 pr-1',
                  isActive
                    ? 'active border-current font-semibold text-copy-nav'
                    : 'border-transparent text-copy-nav hover:border-current',
                  'transition-all duration-300 ease-in-out'
                )}
                onClick={() => {
                  remember(to);
                  navigate(to);
                }}
              >
                {key === 'nav:jobs.planner' ? t('translation:planner') : t(key)}
              </Navbar.Item>
            );
          })}
        </div>

        {/* Config directly after nav items, sliding open to the right */}
        {isOwner && (
          <NavSettingsPanel
            isOpen={shouldShowSettings}
            onToggle={() => setShowSettingsAndSave(!showSettings)}
            menuItems={settingsMenuItems}
            hasItems={hasSettingsItems}
          />
        )}

        {/* Desktop add button at far right */}
        <div className='ml-auto flex items-center pl-2'>
          <Link className='add-button' to='new'>
            +{' '}
            {location.pathname.startsWith('/automations')
              ? t('automation')
              : location.pathname.startsWith('/job-templates')
              ? t('template')
              : t('job')}
          </Link>
        </div>
      </Navbar>
    </div>
  );
};

export function JobsNav({
  unreadActivity,
  monthlyControls,
  selectMode,
  onSelectJobs,
}: {
  unreadActivity?: number;
  monthlyControls?: MonthlyControls;
} & SelectModeNavProps = {}) {
  const { isMobile } = useBreakpoint();
  return (
    <>
      <Mobile
        monthlyControls={monthlyControls}
        selectMode={selectMode}
        onSelectJobs={onSelectJobs}
      />
      {!isMobile && <Desktop unreadActivity={unreadActivity} />}
    </>
  );
}
